/**
 * Sewing Factory Board — the headline strip above the floor panels. See `design-notes.md`.
 *
 * The factory-level twin of `sewing-floor-board/cards.tsx`, deliberately shaped the same so the two
 * boards cannot drift apart. Report-local cards rendering server-computed values (§4.6) — nothing
 * promoted to a shared component. Factory efficiency and factory WFT arrive from the `totals` view
 * already weighted; they are never averaged out of the floor percentages in the browser (§4.3).
 */

import { faTrophy } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { lineStatusLabel, lineStatusTone } from '@/reports/sewing-line-status';

import { count, num, pct, PODIUM, str } from './tokens';
import type { FactoryRow, Tier } from './tokens';

/** One row of the `totals` view. */
export type FactoryTotals = Record<string, unknown>;

interface Scale {
  strip: string;
  label: string;
  figure: string;
  against: string;
  note: string;
  chip: string;
  chipId: string;
  chipValue: string;
  chipIcon: string;
  countChip: string;
}

/** The same tiers the panels use. `md` and `sm` share a size: below `md` the panels are the constraint. */
const SCALE: Record<Tier, Scale> = {
  xl: {
    chipIcon: 'size-5',
    strip: 'gap-x-10 px-6 py-4',
    label: 'text-base tracking-widest',
    figure: 'text-6xl',
    against: 'text-xl',
    note: 'text-base',
    chip: 'gap-2.5 rounded-lg px-4 py-1.5',
    chipId: 'text-2xl',
    chipValue: 'text-xl',
    countChip: 'rounded-lg px-3 py-1 text-xl',
  },
  lg: {
    chipIcon: 'size-4',
    strip: 'gap-x-8 px-6 py-3',
    label: 'text-sm tracking-widest',
    figure: 'text-5xl',
    against: 'text-lg',
    note: 'text-sm',
    chip: 'gap-2 rounded-lg px-3 py-1',
    chipId: 'text-xl',
    chipValue: 'text-lg',
    countChip: 'rounded-lg px-2.5 py-0.5 text-lg',
  },
  md: {
    chipIcon: 'size-3.5',
    strip: 'gap-x-8 px-6 py-2',
    label: 'text-xs tracking-widest',
    figure: 'text-4xl',
    against: 'text-base',
    note: 'text-xs',
    chip: 'gap-2 rounded-md px-2.5 py-0.5',
    chipId: 'text-lg',
    chipValue: 'text-base',
    countChip: 'rounded-md px-2 py-0.5 text-sm',
  },
  sm: {
    chipIcon: 'size-3.5',
    strip: 'gap-x-8 px-6 py-2',
    label: 'text-xs tracking-widest',
    figure: 'text-4xl',
    against: 'text-base',
    note: 'text-xs',
    chip: 'gap-2 rounded-md px-2.5 py-0.5',
    chipId: 'text-lg',
    chipValue: 'text-base',
    countChip: 'rounded-md px-2 py-0.5 text-sm',
  },
  desk: {
    chipIcon: 'size-3',
    strip: 'gap-x-8 px-1 py-3',
    label: 'text-xs tracking-widest',
    figure: 'text-3xl',
    against: 'text-sm',
    note: 'text-xs',
    chip: 'gap-2 rounded-md px-2.5 py-1',
    chipId: 'text-sm',
    chipValue: 'text-sm',
    countChip: 'rounded-md px-2 py-0.5 text-xs',
  },
};

/** A headline figure and what it is measured against. `against` is required — §4.6: a figure with a target shows it. */
function Figure({
  label,
  value,
  against,
  tone,
  note,
  after,
  s,
}: {
  label: string;
  value: string;
  against: string;
  tone: string;
  note?: string;
  /**
   * Extra content under the figure (status chips). Own row so chips wrap inside this column and
   * do not force the whole strip onto a second line.
   */
  after?: ReactNode;
  s: Scale;
}) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <span className={cn('font-semibold text-muted-foreground uppercase', s.label)}>{label}</span>
      <div className="flex flex-wrap items-baseline gap-2.5">
        <span className={cn('leading-none font-bold tabular-nums', s.figure, tone)}>{value}</span>
        <span className={cn('text-muted-foreground', s.against)}>{against}</span>
      </div>
      {note === undefined ? null : (
        <span className={cn('text-muted-foreground', s.note)}>{note}</span>
      )}
      {after}
    </div>
  );
}

/** A server-returned count beside the figure. The label is text: colour is never the signal (§4.6). */
function CountChip({
  value,
  label,
  tone,
  s,
}: {
  value: string;
  label: string;
  tone: string;
  s: Scale;
}) {
  return (
    <span className={cn('font-bold whitespace-nowrap tabular-nums', tone, s.countChip)}>
      {`${value} ${label}`}
    </span>
  );
}

/** A hairline between fields, as the design has. Decorative, so it is hidden from the reader. */
function Divider() {
  return <div className="w-px shrink-0 self-stretch bg-border" aria-hidden />;
}

/** Colour only, over two server-computed cells of one complete row — the narrow §4.3 case; not a new threshold. */
function toneFor(value: number | null, target: number | null, higherIsBetter: boolean): string {
  if (value === null || target === null) return '';
  const good = higherIsBetter ? value >= target : value <= target;
  return good ? 'text-status-ok-ink' : 'text-status-warning';
}

/**
 * The Day output figure's denominator. Prefers the elapsed-share target; falls back to the
 * whole-day plan rather than to a bare count, so a known target is never dropped (§4.6) — only when
 * neither is loaded does the figure go without one. Carried across from the floor board unchanged.
 */
function outputAgainst(output: number | null, targetSoFar: number | null, target: number | null): string {
  if (targetSoFar !== null) {
    const ofToday = target === null ? '' : ` · of ${count(target)} today`;
    return `${count(output)} / ${count(targetSoFar)} pcs so far${ofToday}`;
  }
  if (target !== null) {
    return `${count(output)} / ${count(target)} pcs`;
  }
  return `${count(output)} pcs`;
}

export function FactoryHeadline({
  totals,
  leaders,
  isLoading,
  tier,
}: {
  totals: FactoryTotals | undefined;
  /** Rows of the `floors` view carrying `floor_rank` 1–3, in the order the caller sorted them. */
  leaders: FactoryRow[];
  isLoading: boolean;
  /** The board's measured density, so a dense factory's strip gives back the height its panels need. */
  tier: Tier;
}) {
  const s = SCALE[tier];

  if (isLoading || totals === undefined) {
    return <div className={cn('animate-pulse rounded-lg bg-muted', tier === 'desk' ? 'h-20' : 'h-28')} />;
  }

  const efficiency = num(totals, 'avg_efficiency');
  // The server's effective target; the factory constant stands in only when no roll-up came back.
  const efficiencyTarget =
    num(totals, 'efficiency_target') ?? num(totals, 'factory_efficiency_target');
  const wft = num(totals, 'avg_wft');
  const wftLimit = num(totals, 'wft_limit');
  const output = num(totals, 'day_output_pcs');
  const outputTarget = num(totals, 'day_output_target_pcs');
  const outputTargetSoFar = num(totals, 'day_output_target_so_far');
  const outputPct = num(totals, 'day_output_pct');
  // Two different populations, deliberately (server design-notes.md §5). `lines_*` counts only lines
  // holding a plan of their own — the floor board's definition, so the sentence below means the same
  // thing on both boards. `status_*` buckets every line by the status it was assigned, which is what
  // the chips and the floor panels' pills show.
  const onTarget = num(totals, 'lines_on_target');
  const withTarget = num(totals, 'lines_with_target');
  // status_* = line_status composite (same as floor board STATUS / panel pills).
  // lines_* = own-plan score only — sentence below keeps that distinction.
  const running = num(totals, 'running_line_count');
  const lines = num(totals, 'line_count');
  const floors = num(totals, 'floor_count');
  const planned = withTarget !== null && withTarget > 0; // the server chose planned roll-up vs factory constant; this reads the flag

  /** Headline chips: non-zero line_status counts — same order, words, and colours as panel pills. */
  const statusChips: readonly { field: string; status: string }[] = [
    { field: 'status_not_running', status: 'NOT_RUNNING' },
    { field: 'status_starved', status: 'STARVED' },
    { field: 'status_pace_and_quality', status: 'PACE_AND_QUALITY' },
    { field: 'status_quality', status: 'QUALITY' },
    { field: 'status_no_plan', status: 'NO_PLAN' },
    { field: 'status_below_target', status: 'BELOW_TARGET' },
    { field: 'status_near_target', status: 'NEAR_TARGET' },
    { field: 'status_wip_excess', status: 'WIP_EXCESS' },
    { field: 'status_on_target', status: 'ON_TARGET' },
  ];

  return (
    // One row always: wrap used to drop Lines running + Leading floors under the first three
    // once status chips widened the strip. Columns share width; chips wrap inside their column.
    <div className={cn('flex flex-nowrap items-stretch border-b border-border', s.strip)}>
      <Figure
        s={s}
        label="Factory efficiency"
        value={pct(efficiency)}
        // Names WHICH target: the planned roll-up across the factory, or the factory constant. Different numbers.
        against={
          efficiencyTarget === null
            ? ''
            : `/ ${efficiencyTarget.toFixed(0)}% ${planned ? 'planned' : 'factory'} target`
        }
        tone={toneFor(efficiency, efficiencyTarget, true)}
        note={
          planned
            ? `${count(onTarget)} of ${count(withTarget)} lines on their own target`
            : `${count(running)} of ${count(lines)} lines running`
        }
      />
      <Divider />
      <Figure
        s={s}
        label="Day output"
        value={pct(outputPct, 0)}
        against={outputAgainst(output, outputTargetSoFar, outputTarget)}
        tone=""
        note={outputTarget === null ? 'No plan loaded for this factory today' : undefined}
      />
      <Divider />
      <Figure
        s={s}
        label="Factory WFT"
        value={pct(wft, 2)}
        against={wftLimit === null ? '' : `/ ${wftLimit.toFixed(1)}% limit`}
        tone={toneFor(wft, wftLimit, false)}
        note={`${count(running)} of ${count(lines)} lines running`}
      />
      <Divider />
      <Figure
        s={s}
        label="Lines running"
        value={count(running)}
        against={`of ${count(lines)}`}
        tone=""
        note={`across ${count(floors)} floors`}
        after={
          <span className="flex flex-wrap gap-2">
            {statusChips.map((chip) => {
              const value = num(totals, chip.field);
              if (value === null || value === 0) return null;
              return (
                <CountChip
                  key={chip.field}
                  s={s}
                  value={count(value)}
                  label={lineStatusLabel(chip.status)}
                  tone={lineStatusTone(chip.status).wash}
                />
              );
            })}
          </span>
        }
      />
      <Divider />

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className={cn('font-semibold text-muted-foreground uppercase', s.label)}>
          Leading floors
        </span>
        {leaders.length === 0 ? (
          <span className={cn('text-muted-foreground', s.against)}>No floor is running.</span>
        ) : (
          <div className="flex flex-wrap gap-2">
            {leaders.map((floor) => {
              const rank = num(floor, 'floor_rank') ?? 0;
              const label = str(floor, 'floor_label');
              return (
                <span
                  key={`${label}-${String(rank)}`}
                  className={cn(
                    'flex items-center border border-status-ok/40 bg-status-ok/10',
                    s.chip,
                  )}
                >
                  {/* Decorative, so the rank is repeated as text — colour alone is never the signal (§4.6, §4.9). */}
                  <FontAwesomeIcon
                    icon={faTrophy}
                    className={cn('shrink-0', PODIUM[rank] ?? 'text-muted-foreground', s.chipIcon)}
                    aria-hidden
                  />
                  <span className="sr-only">{`Rank ${String(rank)}: `}</span>
                  <span className={cn('font-bold', s.chipId)}>{label}</span>
                  <span
                    className={cn('font-semibold text-status-ok-ink tabular-nums', s.chipValue)}
                  >
                    {pct(num(floor, 'avg_efficiency'))}
                  </span>
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
